import React, { type CSSProperties } from 'react';
import { useObservableState } from 'observable-hooks';
import { firstValueFrom, map, type Observable } from 'rxjs';
import type { AnySchema } from 'yup';
import { AttributeValueType } from '@/graphql/types';
import classNames from '../../../../../utils/classNames';
import { BaseReportElement } from '../BaseReportElement';
import { createOptionalInsertionPresets } from '../insertionPresetUtils';
import { createBlankImageDefinition, createRuntimeImageInsertionPresets } from './presets';
import { buildImageSettingsYupSchema, normalizeImageSettings } from './settingsYupSchema';
import { createDefaultImageSettings, type ImageAlignment, type ImageSettings } from './contracts';
import {
    createContainCoverChoiceOptions,
    createRuntimeBindingGroupField,
    resolveRuntimeBindingSettingsFormSchema,
    renderRuntimeBindingPlaceholder,
    withRuntimeBindingArgChoiceOptions,
} from '../runtimeBindingFormUtils';
import {
    ATTRIBUTES_IMAGE_BINDING_ID,
    resolveAttributeArgOptions,
} from '../../runtime/dataServices/attributes/argOptions';
import { getBindingCatalog } from '../../runtime/dataServices/bindingCatalog';
import type {
    BuildStructureSettingsYupSchemaHelpers,
    ContextForKind,
    ElementContentRenderResult,
    ElementSettings,
    ElementSettingsFormSchema,
    RenderContextKind,
    RenderRuntime,
    ResolveElementSettingsFormSchemaArgs,
} from '../types';
import type { RuntimeImageAsset } from '../../runtime/dataServices/contracts';
import {
    isRecord,
    normalizeNonEmptyString,
    normalizeString,
} from '../../runtime/dataServices/validationPrimitives';
import { createDeferred } from '../../../../../utils/deferred';

function parseImageAsset(value: unknown): RuntimeImageAsset | null {
    if (value === null) {
        return null;
    }

    if (!isRecord(value)) {
        throw new Error('Expected "image.binding.value" to be an image asset object or null.');
    }

    return {
        url: normalizeNonEmptyString(value.url, 'image.binding.value.url'),
        caption: normalizeString(value.caption, 'image.binding.value.caption'),
    };
}

interface RuntimeImageRendererProps {
    imageAsset$: Observable<RuntimeImageAsset | null>;
    settings: ImageSettings;
    fallbackLabel: string;
    showMissingImagePlaceholder: boolean;
    onLoad: () => void;
    onError: () => void;
}

const IMAGE_ALIGNMENT_STYLES: Record<
    ImageAlignment,
    Pick<CSSProperties, 'marginLeft' | 'marginRight'>
> = {
    left: { marginLeft: 0, marginRight: 'auto' },
    center: { marginLeft: 'auto', marginRight: 'auto' },
    right: { marginLeft: 'auto', marginRight: 0 },
};

function createFigureAlignmentStyle(
    alignment: ImageAlignment | undefined
): CSSProperties | undefined {
    return alignment ? IMAGE_ALIGNMENT_STYLES[alignment] : undefined;
}

function createImageMarginStyle(settings: ImageSettings): CSSProperties | undefined {
    const { marginTop, marginRight, marginBottom, marginLeft } = settings;
    const hasMargins =
        typeof marginTop === 'number' ||
        typeof marginRight === 'number' ||
        typeof marginBottom === 'number' ||
        typeof marginLeft === 'number';

    if (!hasMargins) {
        return undefined;
    }

    return {
        marginTop,
        marginRight,
        marginBottom,
        marginLeft,
    };
}

function createImageSizeStyle(settings: ImageSettings): CSSProperties | undefined {
    if (typeof settings.sizing === 'undefined') {
        return undefined;
    }

    if (settings.sizing === 'auto') {
        return {
            width: '100%',
            height: 'auto',
        };
    }

    return {
        width: settings.width,
        height: settings.height,
    };
}

function createImageFitClassName(settings: ImageSettings): string | undefined {
    if (settings.sizing !== 'custom') {
        return undefined;
    }

    return settings.fit === 'cover' ? 'object-cover' : 'object-contain';
}

function createImageSettingsFormSchema(
    currentSettings?: ElementSettings
): ElementSettingsFormSchema {
    const defaults = createDefaultImageSettings();
    const usesCustomDimensions = currentSettings?.sizing === 'custom';

    return {
        fields: [
            createRuntimeBindingGroupField({
                includeArg: true,
                validateLinkOptions: false,
            }),
            {
                name: 'alt',
                label: gettext('ALT_TEXT'),
                type: 'char',
                required: true,
                default: defaults.alt,
            },
            {
                name: 'sizing',
                label: gettext('ELEMENT_IMAGE_SIZING_LABEL'),
                type: 'choice',
                required: false,
                options: [
                    ['', gettext('ELEMENT_IMAGE_SIZING_DEFAULT_LABEL')],
                    ['auto', gettext('ELEMENT_IMAGE_SIZING_AUTO_LABEL')],
                    ['custom', gettext('ELEMENT_IMAGE_SIZING_CUSTOM_LABEL')],
                ],
                validateOptions: true,
                default: null,
            },
            ...(usesCustomDimensions
                ? [
                      {
                          name: 'fit',
                          label: gettext('FIT'),
                          type: 'choice' as const,
                          required: true,
                          options: createContainCoverChoiceOptions(),
                          default: 'contain',
                      },
                      {
                          name: 'width',
                          label: gettext('WIDTH'),
                          helpText: gettext('ELEMENT_IMAGE_DIMENSION_HELP_TEXT'),
                          type: 'char' as const,
                          required: true,
                          default: null,
                      },
                      {
                          name: 'height',
                          label: gettext('HEIGHT'),
                          helpText: gettext('ELEMENT_IMAGE_DIMENSION_HELP_TEXT'),
                          type: 'char' as const,
                          required: true,
                          default: null,
                      },
                  ]
                : []),
            {
                name: 'alignment',
                label: gettext('ELEMENT_IMAGE_ALIGNMENT_LABEL'),
                type: 'choice',
                required: false,
                options: [
                    ['', gettext('ELEMENT_IMAGE_ALIGNMENT_DEFAULT')],
                    ['left', gettext('ELEMENT_LAYOUT_ALIGNMENT_LEFT')],
                    ['center', gettext('ELEMENT_LAYOUT_ALIGNMENT_CENTER')],
                    ['right', gettext('ELEMENT_LAYOUT_ALIGNMENT_RIGHT')],
                ],
                default: null,
            },
            {
                name: 'marginTop',
                label: gettext('ELEMENT_LAYOUT_MARGIN_TOP_LABEL'),
                type: 'float',
                required: false,
                minValue: 0,
                default: null,
            },
            {
                name: 'marginRight',
                label: gettext('ELEMENT_LAYOUT_MARGIN_RIGHT_LABEL'),
                type: 'float',
                required: false,
                minValue: 0,
                default: null,
            },
            {
                name: 'marginBottom',
                label: gettext('ELEMENT_LAYOUT_MARGIN_BOTTOM_LABEL'),
                type: 'float',
                required: false,
                minValue: 0,
                default: null,
            },
            {
                name: 'marginLeft',
                label: gettext('ELEMENT_LAYOUT_MARGIN_LEFT_LABEL'),
                type: 'float',
                required: false,
                minValue: 0,
                default: null,
            },
        ],
    };
}

function RuntimeImageRenderer({
    imageAsset$,
    settings,
    fallbackLabel,
    showMissingImagePlaceholder,
    onLoad,
    onError,
}: RuntimeImageRendererProps): JSX.Element | null {
    const imageAsset = useObservableState(imageAsset$, null);

    if (!imageAsset) {
        return showMissingImagePlaceholder ? renderRuntimeBindingPlaceholder(fallbackLabel) : null;
    }

    return (
        <div className="flow-root">
            <div style={createImageMarginStyle(settings)}>
                <figure
                    className={classNames(
                        'm-0 flex max-w-full flex-col gap-1',
                        settings.sizing === 'auto' ? 'w-full' : 'w-fit'
                    )}
                    style={createFigureAlignmentStyle(settings.alignment)}
                >
                    <img
                        src={imageAsset.url}
                        alt={settings.alt}
                        className={classNames(
                            'block max-w-full',
                            createImageFitClassName(settings)
                        )}
                        style={createImageSizeStyle(settings)}
                        onLoad={onLoad}
                        onError={onError}
                    />
                    {imageAsset.caption ? (
                        <figcaption className="text-xs text-gray-600">
                            {imageAsset.caption}
                        </figcaption>
                    ) : null}
                </figure>
            </div>
        </div>
    );
}

export class ImageElement extends BaseReportElement<RenderContextKind, ImageSettings> {
    static readonly elementType = 'image';

    static readonly layout = { flow: 'fixed' } as const;

    static readonly compatibleContextKinds = ['project', 'measuring-point'] as const;

    static readonly settingsFormSchemaDependencies = ['sizing'] as const;

    static get insertion() {
        const presets = createRuntimeImageInsertionPresets();

        return {
            label: gettext('IMAGE'),
            supportedSlots: ['child-list'] as const,
            createDefinition: createBlankImageDefinition,
            ...createOptionalInsertionPresets(presets),
        };
    }

    static get settingsFormSchema(): ElementSettingsFormSchema {
        return createImageSettingsFormSchema();
    }

    static async resolveSettingsFormSchema({
        contextKind,
        context,
        client,
        currentSettings,
    }: ResolveElementSettingsFormSchemaArgs): Promise<ElementSettingsFormSchema> {
        const settingsFormSchema = resolveRuntimeBindingSettingsFormSchema({
            contextKind,
            settingsFormSchema: createImageSettingsFormSchema(currentSettings),
            includeLinksRequiringArg: true,
            valueKind: 'image',
        });

        const bindingId = isRecord(currentSettings.binding)
            ? currentSettings.binding.bindingId
            : null;
        const selectedBindingId = typeof bindingId === 'string' ? bindingId.trim() : '';
        const descriptor = selectedBindingId
            ? getBindingCatalog()
                  .listForContextAndValueKind(contextKind, 'image')
                  .find((candidate) => candidate.bindingId === selectedBindingId)
            : undefined;

        const declaredArgOptions = descriptor?.link.argOptions;
        if (declaredArgOptions?.length) {
            return withRuntimeBindingArgChoiceOptions(settingsFormSchema, declaredArgOptions);
        }

        // Attribute definitions are the one image arg source fetched per entity.
        if (descriptor?.bindingId === ATTRIBUTES_IMAGE_BINDING_ID) {
            return withRuntimeBindingArgChoiceOptions(
                settingsFormSchema,
                await resolveAttributeArgOptions({
                    client,
                    contextKind,
                    context,
                    valueType: AttributeValueType.Image,
                    currentArg:
                        isRecord(currentSettings.binding) &&
                        typeof currentSettings.binding.arg === 'string'
                            ? currentSettings.binding.arg
                            : undefined,
                })
            );
        }

        return settingsFormSchema;
    }

    static normalizeSettingsForSave(
        _currentSettings: ElementSettings,
        nextSettings: ElementSettings
    ): ElementSettings {
        if (nextSettings.sizing === 'custom') {
            return nextSettings;
        }

        const normalizedSettings = { ...nextSettings };
        if (nextSettings.sizing !== 'auto') {
            delete normalizedSettings.sizing;
        }
        delete normalizedSettings.fit;
        delete normalizedSettings.width;
        delete normalizedSettings.height;
        return normalizedSettings;
    }

    static buildStructureSettingsYupSchema(
        _helpers: BuildStructureSettingsYupSchemaHelpers
    ): AnySchema {
        return buildImageSettingsYupSchema();
    }

    protected normalizeSettings(settings: ImageSettings): ImageSettings {
        ImageElement.validateSettings(settings);
        return normalizeImageSettings(settings);
    }

    protected getRuntimeBindings() {
        return this.settings.binding ? [this.settings.binding] : [];
    }

    protected toCompatibleReactNode(
        ctx: ContextForKind<RenderContextKind>,
        runtime: RenderRuntime
    ): ElementContentRenderResult {
        const { binding } = this.settings;
        if (!binding) {
            return {
                node: renderRuntimeBindingPlaceholder(
                    gettext('ELEMENT_IMAGE_PLACEHOLDER_BINDING_REQUIRED')
                ),
                ready: Promise.resolve(),
                hasRenderableContent: Promise.resolve(true),
            };
        }

        const imageAsset$ = runtime
            .observeBinding(binding, { context: ctx })
            .pipe(map((value) => parseImageAsset(value)));
        const imageReady = createDeferred<void>();

        return {
            node: (
                <RuntimeImageRenderer
                    imageAsset$={imageAsset$}
                    settings={this.settings}
                    fallbackLabel={gettext('ELEMENT_IMAGE_PLACEHOLDER_NO_IMAGE')}
                    showMissingImagePlaceholder={runtime.renderSurface !== 'export'}
                    onLoad={() => imageReady.resolve(undefined)}
                    onError={() => imageReady.reject(new Error('Failed to load image asset.'))}
                />
            ),
            ready: firstValueFrom(imageAsset$).then((imageAsset) =>
                imageAsset ? imageReady.promise : undefined
            ),
            hasRenderableContent: Promise.resolve(true),
        };
    }
}
